import { featureGateValue } from '../../statsig';
import { ifInitializedValue } from './firebase';
import { FEATURE_FLAG_KEYS } from './keys';
import { clearFlagFromLocalStorage, createFlagGetter, createFlagSetter } from './localStorage';

/**
 * Selects the remote source a flag's value is resolved from. A localStorage override always takes
 * precedence regardless of provider — this only controls the remote lookup.
 * - 'statsig' (default) resolves the flag from Statsig.
 * - 'none' means there is no remote provider: the flag resolves from a localStorage override or
 *   its default value only. Use this for flags that are only ever toggled manually (e.g. local debugging).
 */
type FlagProvider = 'firebase' | 'statsig' | 'none';

type FlagDetails = {
    key: string;
    defaultValue?: boolean;
    provider?: FlagProvider;
};
type FlagAccessor = {
    key: string;
    defaultValue: boolean;
    getValue: () => boolean;
    setLocalStorageValue: (value: string | number | boolean) => void;
    getLocalStorageValue: () => boolean | undefined;
    clearLocalStorageValue: () => void;
    provider?: FlagProvider;
};

const createFlag = ({ key: remoteKey, defaultValue = false, provider = 'statsig' }: FlagDetails): FlagAccessor => {
    const getLocalStorageValue = createFlagGetter(remoteKey);

    const getValue = (): boolean => {
        const localValue = getLocalStorageValue();

        if (localValue !== undefined) {
            return typeof localValue === 'string' ? localValue === 'true' : !!localValue;
        }

        switch (provider) {
            case 'firebase':
                return ifInitializedValue(remoteKey, defaultValue);
            case 'statsig':
                return featureGateValue(remoteKey, defaultValue);
            case 'none':
                return defaultValue;
            default:
                throw new Error(`Unknown provider: ${provider}`);
        }
    };

    return {
        key: remoteKey,
        getValue,
        defaultValue,
        setLocalStorageValue: createFlagSetter(remoteKey),
        getLocalStorageValue,
        clearLocalStorageValue: clearFlagFromLocalStorage(remoteKey),
        provider,
    };
};

// For info on setting up feature flags, see: docs/featureflags.md
const flags = {
    enableOrgOpenFGAPermissions: createFlag({
        key: 'enable_org_open_fga_permissions_web',
    }),
    enableTLOMilestoneOne: createFlag({
        key: FEATURE_FLAG_KEYS.enableTLOMilestoneOne,
        defaultValue: false,
    }),
    enableVideoPlayerDebug: createFlag({ key: 'tmweb_video_player_debug', provider: 'none' }),
    enableReportPublicResource: createFlag({
        key: 'trust_and_safety_report_from_profile',
        provider: 'statsig',
        defaultValue: true,
    }),
    enableReportLivestreamVideo: createFlag({
        key: 'trust_and_safety_report_from_livestream',
        provider: 'statsig',
        defaultValue: true,
    }),
    enableReportRecordedVideo: createFlag({
        key: 'trust_and_safety_report_from_recording',
        provider: 'statsig',
        defaultValue: true,
    }),
    enablePricingPageParameterization: createFlag({ key: 'enable_pricing_page_parameterization' }),
    enableWebWidgetToolsTab: createFlag({
        key: 'tmweb_enable_web_widget_tools_tab',
        defaultValue: true,
    }),
    enableTeamScheduleWidgetPromotion: createFlag({
        key: 'tmweb_enable_team_schedule_widget_promotion',
        defaultValue: true,
    }),
    enableLiveStreamPrerollAds: createFlag({
        key: 'tmweb_live_stream_preroll_ads',
        provider: 'statsig',
    }),
    excludeFreeLivestreamCopy: createFlag({
        key: 'exclude_free_livestream_copy',
        defaultValue: false,
    }),
    enableOrgAvatars: createFlag({
        key: 'enable_org_avatars',
    }),
    enableOrgBannerColor: createFlag({
        key: 'enable_org_banner_color',
    }),
    enableOrgBannerColorView: createFlag({
        key: 'enable_org_banner_color_view',
    }),
    enableDarkIndigoLogin: createFlag({
        key: 'enable_dark_indigo_login',
        defaultValue: true,
    }),
    enableOrgBoxScores: createFlag({
        key: 'enable_org_box_scores',
        defaultValue: false,
    }),
    enableOrgScheduleFilter: createFlag({
        key: 'enable_org_schedule_filter',
    }),
    enableWebDisplayAds: createFlag({
        key: 'tmweb_enable_global_ad_banners',
        defaultValue: true,
    }),
    enableMaxprepsManualStatsExport: createFlag({
        key: 'maxpreps_manual_stats_export',
        provider: 'statsig',
        defaultValue: false,
    }),
    enablePlayerRemovalDialogUpdate: createFlag({
        key: 'player_removal_dialog_update',
        defaultValue: false,
    }),
    enableLineupRecommendations: createFlag({
        key: 'lineup_recommendations',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableVideoErrorTracking: createFlag({
        key: 'tmweb_enable_video_error_tracking',
        defaultValue: false,
    }),
    enableFamilyTeamPassPurchase: createFlag({
        key: 'enable_family_team_pass_purchase',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableDiscountCodes: createFlag({ key: 'enable_discount_codes', defaultValue: false }),
    enableDiscountsForLoggedOutUsers: createFlag({ key: 'enable_discounts_for_logged_out_users', defaultValue: false }),
    enableBulkEditEvents: createFlag({
        key: 'bulk_edit_events_management',
    }),
    enablePreviewCreditExperience: createFlag({
        key: 'enable_preview_credit_experience',
    }),
    enableMlbRbiNgb: createFlag({
        key: 'add_mlb_rbi_to_ngb',
    }),
    enableBasketballClockScoreboard: createFlag({
        key: 'basketball_clock_scoreboard',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableScoringAdaptiveLatency: createFlag({
        key: 'scoring_adaptive_latency',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableBulkCsvEditUpload: createFlag({
        key: 'bulk_csv_edit_upload',
    }),
    enableGCHome: createFlag({
        key: FEATURE_FLAG_KEYS.enableGCHome,
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanRosterView: createFlag({
        key: 'enable_fan_roster_view',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanPlayerProfile: createFlag({
        key: 'enable_fan_player_profile',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableFanStatsTab: createFlag({
        key: 'enable_fan_stats_tab',
    }),
    enableNearMe: createFlag({ key: 'near_me_toggle', defaultValue: true }),
    enableCoachSeasonSituationalStats: createFlag({
        key: 'enable_coach_season_situational_stats',
        provider: 'statsig',
        defaultValue: false,
    }),
    enableTeamAnnouncements: createFlag({
        key: 'team_announcements',
    }),
    enableSubscriptionUnifiedCheckout: createFlag({
        key: 'enable_subscription_unified_checkout',
    }),
    enableAwsWafRequestWrapper: createFlag({
        key: 'aws_waf_request_wrapper',
        defaultValue: true,
    }),
    enableOrganizationRecaps: createFlag({
        key: 'org_page_recaps',
    }),
    enableFanSprayCharts: createFlag({
        key: 'enable_fan_spray_charts',
        provider: 'statsig',
    }),
    enableFanPlayerProfileClips: createFlag({
        key: 'enable_fan_player_profile_clips',
        provider: 'statsig',
        defaultValue: false,
    }),
    enablePlayerInsightClips: createFlag({
        key: 'player_insight_clips',
    }),
    enablePitchingInsights: createFlag({
        key: 'pitching_insights',
    }),
    enableOnboarding: createFlag({
        key: 'enable_onboarding',
    }),
    enableSituationalStatsPlayerProfile: createFlag({
        key: 'situational_stats_player_profile',
    }),
    enableNewEmptyState: createFlag({
        key: 'enable_new_empty_state',
    }),
    enableDefensiveStatsByPosition: createFlag({
        key: 'fielding_stats_by_position',
    }),
    enableGiftSubscriptions: createFlag({
        key: 'enable_gift_subscriptions',
    }),
    webExternalContentLinksEnabled: createFlag({
        key: 'web_external_content_links_enabled',
    }),
    enableThemeSwitcher: createFlag({
        key: 'enable_theme_switcher',
        provider: 'none',
    }),
};

type FeatureFlagNames = keyof typeof flags;
const flagKeys = Object.keys(flags) as Array<FeatureFlagNames>;

/**
 * Get the current value of either all or selected feature flags & gates.
 */
function getFlagValues(): Record<FeatureFlagNames, boolean>;
function getFlagValues<T extends FeatureFlagNames>(flagNames: Array<T>): { [Key in T]: boolean };
function getFlagValues(flagNames?: Array<FeatureFlagNames>) {
    const selectedFlags = flagNames ? flagNames : flagKeys;
    return selectedFlags.reduce(
        (acc, flagName) => {
            acc[flagName] = flags[flagName].getValue();
            return acc;
        },
        {} as Record<FeatureFlagNames, boolean>,
    );
}

export const FeatureFlags = {
    FLAGS: flags,
    getFlagValues,
    createFlag,
};

/**
 * Get the current value of a feature flag.
 */
export const getFeatureFlag = (flagName: FeatureFlagNames) => flags[flagName].getValue();

export type { FeatureFlagNames };

export { initializeAnalytics } from './firebase';
